import { useMemo } from 'react';
import { useEstimationStore } from '../stores/estimationStore';
import { computeN1, computeN2 } from '../utils/estimation';
import type { EstimationResult } from '../types/estimation';
import type { ProgrammeItem } from '../stores/estimationStore';

/** Cost field mapping: programme category -> EstimationResult field name */
const CATEGORY_TO_COST_FIELD: Record<string, keyof EstimationResult> = {
  infra: 'cost_infra_mix',
  ep: 'cost_ep_mix',
  ecoles: 'cost_ecoles',
  creches: 'cost_creches',
  co: 'cost_co',
  sport: 'cost_sport',
  culturel: 'cost_culturel',
};

/**
 * When programme detail items exist for a category, their summed totalCost
 * replaces the parametric cost for that category. The delta cascades into
 * cost_total, cost_total_mix, cost_low, and cost_high.
 */
function applyProgrammeOverrides(
  base: EstimationResult,
  programmeDetail: ProgrammeItem[],
): EstimationResult {
  if (programmeDetail.length === 0) return base;

  // Group detail items by category and sum totalCost
  const totals: Record<string, number> = {};
  for (const item of programmeDetail) {
    totals[item.category] = (totals[item.category] || 0) + item.totalCost;
  }

  // Override cost fields where detail exists
  const overridden = { ...base };
  let costTotalDelta = 0;
  for (const [category, total] of Object.entries(totals)) {
    if (total <= 0) continue;
    const field = CATEGORY_TO_COST_FIELD[category];
    if (!field) continue;
    const oldCost = (overridden[field] as number) || 0;
    (overridden as Record<string, unknown>)[field] = total;
    costTotalDelta += total - oldCost;
  }

  // Update cost_total, cost_total_mix, cost_low, cost_high
  if (costTotalDelta !== 0) {
    const newTotal = overridden.cost_total + costTotalDelta;
    overridden.cost_total = newTotal;
    overridden.cost_total_mix = overridden.cost_total_mix + costTotalDelta;
    overridden.cost_low = Math.round(newTotal * 0.75);
    overridden.cost_high = Math.round(newTotal * 1.35);
  }

  return overridden;
}

/**
 * Client-side estimation engine — reactive hook wrapping pure computation.
 * Uses tenant-specific ratios/benchmarks fetched from the API when available,
 * falling back to hardcoded defaults if the API data hasn't loaded yet.
 * Merges per-project ratio overrides on top of tenant ratios.
 * Applies programme detail overrides when categories have detail items.
 */
export function useEstimation() {
  const ha = useEstimationStore((s) => s.ha);
  const haZi = useEstimationStore((s) => s.haZi);
  const niveau = useEstimationStore((s) => s.niveau);
  const n2Pct = useEstimationStore((s) => s.n2Pct);
  const n2EcoleCT = useEstimationStore((s) => s.n2EcoleCT);
  const tenantRatios = useEstimationStore((s) => s.tenantRatios);
  const tenantBenchmarks = useEstimationStore((s) => s.tenantBenchmarks);
  const projectRatioOverrides = useEstimationStore((s) => s.projectRatioOverrides);
  const programmeDetail = useEstimationStore((s) => s.programmeDetail);

  const result = useMemo(() => {
    // Merge project-level overrides on top of tenant ratios
    const mergedRatios = { ...tenantRatios, ...projectRatioOverrides };

    const base = niveau === 2
      ? computeN2(ha, haZi, n2Pct, mergedRatios, tenantBenchmarks, { ecoleCT: n2EcoleCT })
      : computeN1(ha, haZi, mergedRatios, tenantBenchmarks);

    // Apply programme detail overrides (categories with detail items replace parametric costs)
    return applyProgrammeOverrides(base, programmeDetail);
  }, [ha, haZi, niveau, n2Pct, n2EcoleCT, tenantRatios, tenantBenchmarks, projectRatioOverrides, programmeDetail]);

  return result;
}
